import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { MessageCircle, Search } from 'lucide-react';
import { Input } from '@atoms/Input';
import { Spinner } from '@atoms/Spinner';
import { messagingService } from '@services/messagingService';
import { INBOX_QUERY_KEY } from '@utils/messagingCache';
import type { InboxItem } from '@/types';

interface ChatInboxProps {
  selectedPartnerId?: string;
  onSelect: (item: InboxItem) => void;
}

export function ChatInbox({ selectedPartnerId, onSelect }: ChatInboxProps) {
  const [search, setSearch] = useState('');

  const { data: inbox, isLoading } = useQuery({
    queryKey: INBOX_QUERY_KEY,
    queryFn: () => messagingService.getInbox(),
    staleTime: 15_000,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr');
    if (!q) return inbox ?? [];
    return (inbox ?? []).filter((item) => {
      const haystack = [item.partner.name, item.lastMessage.content]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('tr');
      return haystack.includes(q);
    });
  }, [inbox, search]);

  if (isLoading) return <Spinner />;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-slate-200 p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Konuşma ara…"
            className="pl-9"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {!filtered.length ? (
          <p className="p-6 text-center text-sm text-slate-500">Henüz konuşma yok</p>
        ) : (
          filtered.map((item) => (
            <button
              key={item.partnerId}
              type="button"
              onClick={() => onSelect(item)}
              className={clsx(
                'flex w-full items-start gap-3 border-b border-slate-100 px-4 py-3 text-left transition-colors hover:bg-slate-50',
                selectedPartnerId === item.partnerId && 'bg-brand-50',
              )}
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                <MessageCircle className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate font-medium text-slate-900">{item.partner.name}</p>
                  {item.unreadCount > 0 && (
                    <span className="rounded-full bg-brand-500 px-2 py-0.5 text-[10px] font-bold text-white">
                      {item.unreadCount}
                    </span>
                  )}
                </div>
                <p className="mt-1 truncate text-sm text-slate-600">{item.lastMessage.content}</p>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
